import { Controller } from "@hotwired/stimulus"

// A matched field's lookup (shared/_lookup): typing lists the entries on the
// page (a JSON list of { name, to, note } by id, `to` what picking it puts
// in the field), those starting with what's typed first, then "＋ New
// brand “…”" when it's none of them. The picked one shows as its tag.
export default class extends Controller {
  static targets = ["value", "chip", "chipText", "input", "list"]
  static values = { source: String, kind: String }

  connect() {
    this.form = this.element.closest("form")
    this.typed = () => { if (this.inputTarget.value.trim()) this.valueTarget.value = this.inputTarget.value.trim() }
    this.form?.addEventListener("submit", this.typed)
  }

  disconnect() { this.form?.removeEventListener("submit", this.typed) }

  entries() {
    if (!this.cache) {
      try { this.cache = JSON.parse(document.getElementById(this.sourceValue)?.textContent || "[]") } catch { this.cache = [] }
    }
    return this.cache
  }

  focus(event) { if (!event.target.closest("button")) this.inputTarget.focus() }

  search() {
    const typed = this.inputTarget.value.trim()
    const q = key(typed)
    const found = this.entries().filter(e => !q || key(e.name).includes(q))
    const hits = [...found.filter(e => key(e.name).startsWith(q)), ...found.filter(e => !key(e.name).startsWith(q))].slice(0, 6)
    const rows = hits.map(e => this.row(e.name === e.to ? e.name : `${e.name} → ${e.to}`, e.note, () => this.pick(e.to, false)))
    if (typed && !this.entries().some(e => key(e.name) === q)) {
      rows.push(this.row(`＋ New ${this.kindValue} “${typed}”`, `not one you have`, () => this.pick(typed, true), "lk-new"))
    }
    this.listTarget.replaceChildren(...rows)
    this.hi = rows.length ? 0 : -1
    this.mark()
    this.open(rows.length > 0)
  }

  row(text, note, pick, extra = "") {
    const row = Object.assign(document.createElement("div"), { className: `lk-opt ${extra}` })
    row.setAttribute("role", "option")
    row.append(Object.assign(document.createElement("span"), { textContent: text }),
      Object.assign(document.createElement("span"), { className: "lk-note", textContent: note }))
    row.addEventListener("mousedown", event => { event.preventDefault(); pick() })
    row.pick = pick
    return row
  }

  pick(name, isNew) {
    this.valueTarget.value = name
    this.chipTextTarget.textContent = `${isNew ? "＋ New: " : "✓ "}${name}`
    this.chipTarget.classList.toggle("known", !isNew)
    this.chipTarget.classList.toggle("new", isNew)
    this.chipTarget.hidden = false
    this.inputTarget.value = ""
    this.open(false)
  }

  clear() {
    this.valueTarget.value = ""
    this.chipTarget.hidden = true
    this.inputTarget.focus()
  }

  key(event) {
    const rows = [...this.listTarget.children]
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (this.listTarget.hidden) return this.search()
      event.preventDefault()
      this.hi = (this.hi + (event.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length
      this.mark()
    } else if (event.key === "Enter" && !this.listTarget.hidden && rows[this.hi]) {
      event.preventDefault()
      rows[this.hi].pick()
    } else if (event.key === "Escape") {
      this.open(false)
    } else if (event.key === "Backspace" && !this.inputTarget.value && !this.chipTarget.hidden) {
      this.clear()
    }
  }

  mark() { [...this.listTarget.children].forEach((row, i) => row.classList.toggle("hi", i === this.hi)) }

  close() { this.open(false) }

  open(on) {
    this.listTarget.hidden = !on
    this.inputTarget.setAttribute("aria-expanded", on)
  }
}

// What's typed and a name, compared without case, spaces or punctuation ("in art" is "In Art Action"'s start).
const key = text => text.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "")
